import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleContent from './components/ArticleContent';
import RelatedArticles from './components/RelatedArticles';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');
  const relatedArticles = article ? getRelatedArticles(article.slug, 4) : [];

  if (!article) {
    return (
      <>
        <SEOHead
          title="文章未找到 | 今日看料"
          description="抱歉，您查找的文章不存在或已被删除。请返回首页浏览更多精彩内容。"
          keywords="今日看料,文章未找到"
          canonicalUrl="http://rtxn.cn/"
        />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-20">
            <div className="max-w-3xl mx-auto px-4 py-20 text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-background-100 flex items-center justify-center">
                <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
              </div>
              <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-3">文章未找到</h1>
              <p className="text-foreground-500 mb-6">抱歉，您查找的文章不存在或已被删除。</p>
              <Link
                to="/"
                className="inline-flex px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
              >
                返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  return (
    <>
      <SEOHead
        title={`${article.title} | 今日看料`}
        description={article.summary}
        keywords={`${article.tags.join(',')},今日看料,${article.categoryName}`}
        canonicalUrl={`http://rtxn.cn/article/${article.slug}`}
        ogType="article"
        pageType="article"
        articleData={{
          publishDate: article.publishDate,
          modifiedDate: article.publishDate,
          authorName: article.authorName,
          category: article.categoryName,
          tags: article.tags,
        }}
        breadcrumbItems={[
          { name: '首页', url: 'http://rtxn.cn/' },
          { name: article.categoryName, url: `http://rtxn.cn/category/${article.category}` },
          { name: article.title, url: `http://rtxn.cn/article/${article.slug}` },
        ]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <nav className="max-w-3xl mx-auto px-4 md:px-6 pt-6" aria-label="面包屑导航">
            <div className="flex items-center gap-1.5 text-xs text-foreground-500">
              <Link to="/" className="hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer">
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-[10px]"></i>
              <Link
                to={`/category/${article.category}`}
                className="hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
              >
                {article.categoryName}
              </Link>
              <i className="ri-arrow-right-s-line text-[10px]"></i>
              <span className="text-foreground-700 truncate max-w-[200px]">{article.title}</span>
            </div>
          </nav>

          <ArticleContent article={article} />
        </main>

        <RelatedArticles articles={relatedArticles} />

        <Footer />
      </div>
    </>
  );
}